import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { Sidebar } from "@/components/Sidebar";
import Dashboard from "@/pages/Dashboard";
import LabelCreator from "@/pages/LabelCreator";
import ProductLibrary from "@/pages/ProductLibrary";
import LabelHistory from "@/pages/LabelHistory";
import Login from "@/pages/Login";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("meatprint_token");
  const isPublicAuth = config.url?.includes("/auth/login") || config.url?.includes("/auth/recover");
  if (token && !isPublicAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      const url = err.config?.url || "";
      const isPublicAuth = url.includes("/auth/login") || url.includes("/auth/recover");
      if (!isPublicAuth) {
        localStorage.removeItem("meatprint_token");
        localStorage.removeItem("meatprint_user");
        window.location.reload();
      }
    }
    return Promise.reject(err);
  }
);

function App() {
  const [token, setToken] = useState(localStorage.getItem("meatprint_token"));
  const [checking, setChecking] = useState(true);

  const verifyToken = useCallback(async () => {
    if (!token) {
      setChecking(false);
      return;
    }
    try {
      await axios.get(`${API}/auth/verify`);
      // Token valid, seed products
      axios.post(`${API}/seed`).catch(() => {});
    } catch {
      localStorage.removeItem("meatprint_token");
      localStorage.removeItem("meatprint_user");
      setToken(null);
    } finally {
      setChecking(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const handleLogin = (newToken) => {
    setToken(newToken);
    axios.post(`${API}/seed`).catch(() => {});
  };

  const handleLogout = () => {
    localStorage.removeItem("meatprint_token");
    localStorage.removeItem("meatprint_user");
    setToken(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center mx-auto mb-3 animate-pulse-glow">
            <span className="text-primary-foreground font-bold text-xl font-mono">M</span>
          </div>
          <p className="text-xs text-muted-foreground tracking-widest uppercase">LOADING...</p>
        </div>
      </div>
    );
  }

  if (!token) {
    return (
      <>
        <Login onLogin={handleLogin} />
        <Toaster theme="dark" position="top-right" richColors />
      </>
    );
  }

  return (
    <BrowserRouter>
      <div className="flex min-h-screen bg-background noise-bg">
        <Sidebar onLogout={handleLogout} />
        <main className="flex-1 ml-0 lg:ml-[240px]">
          <div className="p-4 pt-16 lg:pt-4 md:p-6 lg:p-8 max-w-[1400px]">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/create" element={<LabelCreator />} />
              <Route path="/products" element={<ProductLibrary />} />
              <Route path="/history" element={<LabelHistory />} />
            </Routes>
          </div>
        </main>
        <Toaster theme="dark" position="top-right" richColors />
      </div>
    </BrowserRouter>
  );
}

export default App;
